import React, { useEffect, useMemo, useState } from 'react';
import { ArrowLeftIcon } from 'cokitchen-icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@cokitchen/cokitchen-components';
import { ReactComponent as PenIcon } from 'assets/icons/pen.svg';
import { ReactComponent as UploadIcon } from 'assets/icons/upload-icon.svg';
import MonthSelector, { WeekType } from 'components/kitchen/month-selector';
import moment from 'moment';
import RosterCard from 'components/kitchen/roster-card';
import { apiService } from 'utils/api';
import { ROSTER_SHIFT } from 'utils/enum';
import UploadRosterModal from 'components/kitchen/upload-roster-modal';
import { KITCHEN_ROUTE_KEYS } from 'utils/constants';
import { KitchenShiftType } from 'models/kitchen-roster.model';
import RosterFilter, { ENUM_ROASTER_SEARCH_TYPES } from './partials/roster-page-filter';
import useWeeklyRosterContext from 'contexts/weekly-roster.context';
import { transformRoster } from './utils';

const WeeklyRoster = () => {
  const navigate = useNavigate();
  const { setAddRosterFormData } = useWeeklyRosterContext();

  const [searchParams] = useSearchParams();
  const startDate = searchParams.get('start_date');
  const endDate = searchParams.get('end_date');
  const upload = searchParams.get('upload');

  const initMonth =
    startDate && endDate
      ? {
          start: moment(startDate),
          end: moment(endDate)
        }
      : {
          start: moment().startOf('week'),
          end: moment().endOf('week')
        };

  const [search, setSearch] = useState({ val: '', type: ENUM_ROASTER_SEARCH_TYPES.NAME });
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [roster, setRoster] = useState<KitchenShiftType[]>([]);
  const [week, setWeek] = useState<WeekType>(initMonth);
  const [loading, setLoading] = useState(true);

  const filter = useMemo(() => {
    return {
      department: search.type === ENUM_ROASTER_SEARCH_TYPES.DEPT ? debouncedSearch : undefined,
      staff_name: search.type === ENUM_ROASTER_SEARCH_TYPES.NAME ? debouncedSearch : undefined,
      from_date: moment(week.start).format('YYYY-MM-DD'),
      end_date: moment(week.end).format('YYYY-MM-DD')
    };
  }, [debouncedSearch, week]);

  const dayData = roster?.[0]?.shifts;

  const getRoster = async () => {
    setLoading(true);
    try {
      const res = await apiService.get('kitchen/rosters/list', {
        baseUrl: process.env.REACT_APP_KITCHEN_API_URL,
        params: filter
      });
      setRoster(res.data.data);
    } catch (err) {
      console.log('err', err);
    } finally {
      setLoading(false);
    }
  };

  const getEditRoute = () =>
    `${KITCHEN_ROUTE_KEYS.EDIT_WEEKLY_ROSTER}?start_date=${week.start.format(
      'YYYY-MM-DD'
    )}&end_date=${week.end.format('YYYY-MM-DD')}`;

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearch(search.val);
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [search]);

  useEffect(() => {
    getRoster();
  }, [filter]);

  useEffect(() => {
    if (upload && upload === 'true') setShowUploadModal(true);
  }, [upload]);

  const currentWeekUploaded =
    dayData &&
    (dayData.afternoon.length > 0 || dayData.morning.length > 0 || dayData.night.length > 0);

  return (
    <div className='flex flex-col gap-5'>
      <div className='flex self-start items-center gap-3 cursor-pointer'>
        <span className='p-2 text-primary bg-[#F4F1F1] rounded-full' onClick={() => navigate(-1)}>
          <ArrowLeftIcon color='#E60A2B' />
        </span>
        <p className='text-sm font-medium'>Return to settings</p>
      </div>

      <div className='flex flex-col gap-8'>
        <div className='flex items-center justify-between gap-3'>
          <div className='flex flex-col gap-[2px]'>
            <p className='text-xl text-[#1A1A1A] font-semibold'>Roster</p>
            <p className='text-sm text-[#595959]'>
              Manage and update team members and shift arrangement
            </p>
          </div>

          <div className='flex items-center gap-3'>
            {currentWeekUploaded && (
              <Button
                small
                variant='block'
                color='transparent'
                className='w-[145px] h-10 flex items-center gap-1 bg-[#F2F2F2] text-[#1A1A1A]'
                onClick={() => {
                  if (roster.length > 0) {
                    setAddRosterFormData(transformRoster(roster));
                  }
                  navigate(getEditRoute());
                }}>
                <PenIcon className='shrink-0 w-[18px] [&_path]:fill-[#1A1A1A]' />
                Edit
              </Button>
            )}
            <Button
              small
              variant='block'
              color='primary'
              className=' h-10 flex items-center gap-1 text-sm'
              onClick={() => setShowUploadModal(true)}>
              <UploadIcon className='shrink-0 size-4' />
              Upload
            </Button>
          </div>
        </div>

        <div className='flex items-center justify-between gap-3'>
          <MonthSelector value={week} onChange={setWeek} />

          <RosterFilter searchVal={search} setSearchVal={setSearch} />
        </div>
        {loading ? (
          <p className='text-center text-red-5 font-semibold animate-bounce'>Loading...</p>
        ) : (
          <>
            {dayData ? (
              <div className='grid grid-cols-[repeat(auto-fill,minmax(400px,1fr))] gap-6'>
                <RosterCard shift={ROSTER_SHIFT.MORNING} data={dayData.morning} />
                <RosterCard shift={ROSTER_SHIFT.AFTERNOON} data={dayData.afternoon} />
                <RosterCard shift={ROSTER_SHIFT.NIGHT} data={dayData.night} />
              </div>
            ) : (
              <p className='text-center text-[#595959] text-sm'>
                No roster data available for this week.
              </p>
            )}
          </>
        )}
      </div>
      {showUploadModal && (
        <UploadRosterModal
          show={showUploadModal}
          onClose={() => setShowUploadModal(false)}
          defaultWeek={week}
        />
      )}
    </div>
  );
};

export default WeeklyRoster;
